/**
 * Mirroring a project into a folder on this machine.
 *
 * WHAT THIS MODULE IS, AND IS NOT
 *   It is the thin middle of folder synchronisation, and deliberately holds no
 *   judgement of its own:
 *     - WHICH FILES DIFFER, and what should happen to each, is decided by
 *       @lattice/core (syncplan.ts). Nothing here re-implements a comparison.
 *     - EVERY BYTE of disk I/O belongs to the Electron shell, reached through
 *       the folder bridge (hooks/useDesktop.ts). Nothing here opens a file.
 *   What is left is: ask the shell what is on disk, ask the replica what
 *   Lattice holds, hand both to `buildSyncPlan`, remember what the person
 *   chose, and drive the shell through the operations that result.
 *
 * WHY A SEPARATE STORE
 *   The replica (workspace.ts) is shared truth that arrives from the server.
 *   A folder link is the opposite: it is true of ONE machine, it never leaves
 *   it, and no colleague can see it. Mixing the two would mean a sync delta
 *   could touch a local folder, which is exactly the coupling this product
 *   does not want.
 *
 * DATA DEFINITIONS
 *
 *   A FolderLink is { projectId, dir, base, linkedAt, lastSyncedAt? }.
 *   Interpretation: this project is mirrored into `dir` on this machine, and
 *   `base` is the PIVOT — what both sides held when the last sync finished.
 *   Without the pivot, "the two differ" cannot be turned into "who changed
 *   it", which is the whole reason a three-way comparison exists.
 *
 *   A Scan is { projectId, files, at, skippedHashes }.
 *   Interpretation: what was on disk at time `at`. `skippedHashes` counts
 *   files the shell could not read — typically a part open in SolidWorks —
 *   and is shown rather than swallowed, because a file missing from a scan
 *   otherwise looks exactly like a file somebody deleted.
 *
 *   A Phase is "idle" | "choosing" | "scanning" | "applying".
 *   Interpretation: what the folder machinery is doing right now. Only one
 *   thing at a time, per window: two scans of one directory answer the same
 *   question twice and can disagree about which answer is newer.
 *
 *   A Progress is { phase, done, total, bytesDone, bytesTotal, path? }.
 *   Interpretation: how far into that work we are, in both files and bytes,
 *   because 900 tiny drawings and 900 assemblies take very different times.
 *
 *   An Override is an entry in a Map from path to Resolution.
 *   Interpretation: a choice the person made EXPLICITLY. Absence means "use
 *   the item's own default", which is how incoming rows start at Accept,
 *   outgoing rows at Keep local, and conflicts at neither — without this
 *   module ever restating those rules.
 *
 *   A RunReport is { at, kind, applied, bytes, failures, cancelled }.
 *   Interpretation: what one press of Pull, Push or Sync actually did.
 */
import { create } from 'zustand'
import {
  actionFor, buildSyncPlan, nextManifest, parseSha256,
  type FileFacts, type FileSet, type LockMap, type Manifest, type Path,
  type ProjectId, type Resolution, type SyncItem, type SyncPlan, type UserId,
} from '@lattice/core'
import {
  folderBridge,
  type FolderOperation, type ManifestPairs, type OperationResult,
} from '@/hooks/useDesktop'
import { api, ApiError } from '@/store/api'

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type Phase = 'idle' | 'choosing' | 'scanning' | 'applying'

export interface FolderLink {
  readonly projectId: string
  readonly dir: string
  readonly base: Manifest
  readonly linkedAt: string
  readonly lastSyncedAt?: string
}

export interface Scan {
  readonly projectId: string
  readonly files: Manifest
  readonly at: string
  readonly skippedHashes: number
}

export interface Progress {
  readonly phase: Phase
  readonly done: number
  readonly total: number
  readonly bytesDone: number
  readonly bytesTotal: number
  readonly path?: string
}

export interface Failure {
  readonly path: string
  readonly error: string
}

export interface RunReport {
  readonly at: string
  readonly kind: 'pull' | 'push' | 'sync'
  readonly applied: number
  readonly bytes: number
  readonly failures: readonly Failure[]
  readonly cancelled: boolean
}

export const IDLE: Progress = { phase: 'idle', done: 0, total: 0, bytesDone: 0, bytesTotal: 0 }

/* ------------------------------------------------------------------ */
/* The store                                                           */
/* ------------------------------------------------------------------ */

export interface SyncFolderState {
  /** Every project this machine has linked, keyed by project id. */
  readonly links: Readonly<Record<string, FolderLink>>
  /** The most recent scan. One at a time: the Changes screen shows one project. */
  readonly scan?: Scan
  /** Explicit per-path choices for the project in `scan`. */
  readonly overrides: ReadonlyMap<Path, Resolution>
  readonly progress: Progress
  /** The last thing that went wrong, in the person's words, not a stack. */
  readonly error?: string
  readonly report?: RunReport

  readonly setLink: (link: FolderLink) => void
  readonly dropLink: (projectId: string) => void
  readonly setScan: (scan: Scan | undefined) => void
  readonly choose: (path: Path, resolution: Resolution) => void
  readonly chooseMany: (paths: Iterable<Path>, resolution: Resolution) => void
  readonly clearChoices: () => void
  readonly setProgress: (progress: Progress) => void
  readonly setError: (error: string | undefined) => void
  readonly setReport: (report: RunReport | undefined) => void
  readonly reset: () => void
}

export const useSyncFolder = create<SyncFolderState>()((set) => ({
  links: {},
  overrides: new Map(),
  progress: IDLE,

  setLink: link => set(state => ({ links: { ...state.links, [link.projectId]: link } })),

  dropLink: projectId => set(state => {
    const links = { ...state.links }
    delete links[projectId]
    return {
      links,
      // A scan and a set of choices describe a folder that is no longer ours.
      scan: state.scan?.projectId === projectId ? undefined : state.scan,
      overrides: state.scan?.projectId === projectId ? new Map() : state.overrides,
    }
  }),

  // A new scan invalidates every choice: they were made about a different
  // comparison, and silently carrying them over is how a person ends up
  // pushing something they ticked five minutes and one colleague ago.
  setScan: scan => set({ scan, overrides: new Map() }),

  choose: (path, resolution) => set(state => {
    const overrides = new Map(state.overrides)
    overrides.set(path, resolution)
    return { overrides }
  }),

  chooseMany: (paths, resolution) => set(state => {
    const overrides = new Map(state.overrides)
    for (const path of paths) overrides.set(path, resolution)
    return { overrides }
  }),

  clearChoices: () => set({ overrides: new Map() }),
  setProgress: progress => set({ progress }),
  setError: error => set({ error }),
  setReport: report => set({ report }),
  reset: () => set({ links: {}, scan: undefined, overrides: new Map(), progress: IDLE, error: undefined, report: undefined }),
}))

/* ------------------------------------------------------------------ */
/* Crossing the bridge                                                 */
/* ------------------------------------------------------------------ */

/**
 * Rebuild a Manifest from the pairs that crossed the IPC boundary.
 *
 * This is the ONE place an unbranded hash from outside becomes a Sha256, which
 * is what "parse at the boundary" means here: everything downstream may assume
 * a manifest is well-formed.
 *
 * WHY A BAD ENTRY THROWS RATHER THAN BEING DROPPED
 *   A path missing from the LOCAL manifest is indistinguishable from a file
 *   the person deleted, and "deleted here" suggests deleting it in Lattice for
 *   everybody. So a manifest we cannot read must abandon the whole scan, not
 *   quietly shrink. The caller reports the message and the previous answer
 *   stands.
 * manifestFrom : ManifestPairs -> Manifest
 */
export function manifestFrom(pairs: ManifestPairs): Manifest {
  const out = new Map<string, FileFacts>()
  for (const [path, facts] of pairs) {
    if (typeof path !== 'string' || path.length === 0) throw new Error('a file arrived with no path')
    const sha = parseSha256(facts?.sha ?? '')
    if (!sha.ok) throw new Error(`"${path}": ${sha.error}`)
    if (!Number.isFinite(facts.size) || facts.size < 0) throw new Error(`"${path}" has an impossible size`)
    out.set(path, { sha: sha.value, size: facts.size })
  }
  return out
}

/** The inverse, for handing a manifest back to the shell. */
export const manifestPairs = (manifest: Manifest): [string, { sha: string; size: number }][] =>
  [...manifest].map(([path, facts]) => [path, { sha: facts.sha, size: facts.size }])

/* ------------------------------------------------------------------ */
/* Assembling the three sides                                          */
/* ------------------------------------------------------------------ */

/**
 * The remote side of the comparison, taken from the replica.
 *
 * A FileSet is keyed case-insensitively (the tree has to be, because the
 * engineers' Windows machines are) while a Manifest is keyed by the real path,
 * so this re-keys by `entry.path` rather than by the map key. Getting that
 * backwards would compare "parts/roof.step" on disk against "PARTS/ROOF.STEP"
 * in Lattice and report every file as both new and deleted.
 * remoteManifest : FileSet -> Manifest
 */
export const remoteManifest = memoOnce((files: FileSet): Manifest => {
  const out = new Map<string, FileFacts>()
  for (const entry of files.values()) out.set(entry.path, { sha: entry.sha, size: entry.size })
  return out
})

/**
 * Give every path ONE spelling across the three manifests.
 *
 * WHY THIS IS NEEDED
 *   Windows and macOS cannot hold two files whose names differ only by case,
 *   so "Roof.STEP" on disk and "roof.step" in Lattice are the SAME FILE with
 *   two spellings — but a Map keyed by the exact string sees two paths, and
 *   the plan would offer to download one and upload the other, then to delete
 *   whichever the person did not choose. Aligning first turns that into the
 *   ordinary "unchanged" it really is.
 *
 *   The remote spelling wins, then the base's, then whatever is on disk. Some
 *   spelling has to win, and the remote one is the one every colleague sees.
 *
 * COST: a deliberate rename that only changes case is invisible to sync. That
 * is the right trade for a team on Windows, where such a rename cannot even be
 * expressed on the machine doing the syncing.
 * alignSpelling : Manifest Manifest Manifest -> {base, local, remote}
 */
export function alignSpelling(base: Manifest, local: Manifest, remote: Manifest): {
  base: Manifest; local: Manifest; remote: Manifest
} {
  const canonical = new Map<string, string>()
  for (const manifest of [remote, base, local]) {
    for (const path of manifest.keys()) {
      const key = path.toLowerCase()
      if (!canonical.has(key)) canonical.set(key, path)
    }
  }
  /**
   * WHY THIS IS TWO PASSES
   *   A case-sensitive filesystem — Linux, or a Mac formatted that way — CAN
   *   hold "Roof.STEP" and "roof.step" side by side. Rewriting both to one
   *   spelling would drop one of them from the comparison, and a file missing
   *   from the local side is indistinguishable from a file somebody deleted:
   *   the plan would offer to delete a live part from Lattice. So exact
   *   spellings claim their key first, and anything left over keeps its own
   *   name rather than displacing another file. The count never shrinks.
   */
  const rekey = (manifest: Manifest): Manifest => {
    const out = new Map<string, FileFacts>()
    const target = (path: string): string => canonical.get(path.toLowerCase()) ?? path
    for (const [path, facts] of manifest) if (target(path) === path) out.set(path, facts)
    for (const [path, facts] of manifest) {
      if (target(path) === path) continue
      out.set(out.has(target(path)) ? path : target(path), facts)
    }
    return out
  }
  return { base: rekey(base), local: rekey(local), remote: rekey(remote) }
}

/**
 * The plan for one project: what a sync would do, before anybody has chosen.
 *
 * Derived on every render rather than stored, for the same reason the file
 * tree is: a plan kept in a store can disagree with the replica it was built
 * from, and a synchroniser that is confidently out of date is worse than one
 * that is slow. Folding ~900 paths costs well under a millisecond.
 * planFor : {projectId, base, local, files, locks, me} -> SyncPlan
 */
export function planFor(input: {
  projectId: ProjectId
  base: Manifest
  local: Manifest
  files: FileSet
  locks?: LockMap
  me?: UserId
}): SyncPlan {
  const aligned = alignSpelling(input.base, input.local, remoteManifest(input.files))
  return buildSyncPlan({
    projectId: input.projectId,
    base: aligned.base,
    local: aligned.local,
    remote: aligned.remote,
    ...(input.locks ? { locks: input.locks } : {}),
    ...(input.me ? { me: input.me } : {}),
  })
}

/* ------------------------------------------------------------------ */
/* Choices                                                             */
/* ------------------------------------------------------------------ */

/**
 * What is chosen for one item: the person's override, or its own default.
 * resolutionOf : SyncItem Map<Path,Resolution> -> Resolution
 */
export const resolutionOf = (item: SyncItem, overrides: ReadonlyMap<Path, Resolution>): Resolution =>
  overrides.get(item.path) ?? item.suggested

/**
 * The whole resolution map the Sync button would apply.
 * resolutionsFor : SyncPlan Map<Path,Resolution> -> Map<Path,Resolution>
 */
export const resolutionsFor = (
  plan: SyncPlan, overrides: ReadonlyMap<Path, Resolution>,
): Map<Path, Resolution> =>
  new Map(plan.items.map(item => [item.path, resolutionOf(item, overrides)]))

/**
 * The groups the Changes screen draws.
 *
 * Interpretation: the same items, arranged by what the person has to do about
 * them — receive, send, decide, or wait for a colleague. `converged` is listed
 * apart because it needs no decision at all: both sides already hold the same
 * bytes and only our record is stale.
 * groupPlan : SyncPlan -> PlanGroups
 */
export interface PlanGroups {
  readonly incoming: readonly SyncItem[]
  readonly outgoing: readonly SyncItem[]
  readonly conflicts: readonly SyncItem[]
  readonly blocked: readonly SyncItem[]
  readonly converged: readonly SyncItem[]
}

export function groupPlan(plan: SyncPlan): PlanGroups {
  const of = (pred: (item: SyncItem) => boolean): SyncItem[] => plan.items.filter(pred)
  return {
    incoming: of(item => item.state.startsWith('pull')),
    outgoing: of(item => item.state.startsWith('push')),
    conflicts: of(item => item.state === 'conflict'),
    blocked: of(item => item.state === 'blocked'),
    converged: of(item => item.state === 'converged'),
  }
}

/**
 * A sentence for one CHOICE, as opposed to core's `describeItem`, which
 * describes the SITUATION. The difference matters on the two rows where the
 * obvious word is wrong: "keep local" on a file that only exists in Lattice
 * deletes it there, and nobody should discover that afterwards.
 * describeChoice : SyncItem Resolution -> String
 */
export function describeChoice(item: SyncItem, resolution: Resolution): string {
  switch (actionFor(item, resolution)) {
    case 'download': return 'Copy the Lattice version into your folder'
    case 'delete-local': return 'Delete it from your folder'
    case 'upload': return 'Upload your version to Lattice'
    case 'delete-remote': return 'Delete it from Lattice, for everybody'
    case 'record-only': return 'Nothing to move — both sides already match'
    case 'nothing': return item.state === 'blocked'
      ? 'Nothing, while somebody else holds the lock'
      : 'Leave both sides exactly as they are'
  }
}

/* ------------------------------------------------------------------ */
/* Turning choices into work                                           */
/* ------------------------------------------------------------------ */

/**
 * The operations the shell must perform, in the order it should perform them.
 *
 * "record-only" is excluded: it moves nothing, and writing the new manifest at
 * the end of the run already records it. Sending it would be asking the shell
 * to do nothing, one IPC call at a time.
 * operationsFor : SyncPlan Map<Path,Resolution> -> [FolderOperation]
 */
export function operationsFor(
  plan: SyncPlan, resolutions: ReadonlyMap<Path, Resolution>,
): FolderOperation[] {
  const out: FolderOperation[] = []
  for (const item of plan.items) {
    const action = actionFor(item, resolutions.get(item.path) ?? 'skip')
    if (action === 'nothing' || action === 'record-only') continue
    const facts = action === 'download' ? item.remote : action === 'upload' ? item.local : undefined
    out.push({
      path: item.path,
      action,
      ...(facts ? { sha: facts.sha, size: facts.size } : {}),
      // What the plan compared against, so the shell can refuse to overwrite
      // or delete a file that has been saved since. '' says the plan found
      // nothing. Both actions that touch a local file carry it; `upload` and
      // `delete-remote` do not, because neither destroys anything here.
      ...(action === 'download' || action === 'delete-local'
        ? { expect: item.local?.sha ?? '' } : {}),
    })
  }
  return orderOperations(out)
}

/** How destructive each kind of operation is; lower runs first. */
const RISK: Record<FolderOperation['action'], number> = {
  download: 0, upload: 1, 'delete-local': 2, 'delete-remote': 3, 'record-only': 4,
}

/**
 * Receive first, send second, delete last.
 *
 * WHY THE ORDER IS PART OF THE DESIGN
 *   A run can be cancelled, lose a network, or hit a locked file halfway. If
 *   deletions ran first, a person who cancelled would be left with files gone
 *   and their replacements not yet arrived. Doing every additive operation
 *   before any destructive one means an interrupted sync always leaves MORE
 *   data than it started with, never less.
 * orderOperations : [FolderOperation] -> [FolderOperation]
 */
export const orderOperations = (operations: readonly FolderOperation[]): FolderOperation[] =>
  [...operations].sort((a, b) => RISK[a.action] - RISK[b.action] || a.path.localeCompare(b.path))

/** Bytes an operation will actually move; a deletion moves none. */
const bytesOfOperation = (operation?: FolderOperation): number =>
  operation && (operation.action === 'download' || operation.action === 'upload')
    ? operation.size ?? 0
    : 0

/** bytesOf : [FolderOperation] -> Number */
export const bytesOf = (operations: readonly FolderOperation[]): number =>
  operations.reduce((sum, operation) => sum + bytesOfOperation(operation), 0)

/* ------------------------------------------------------------------ */
/* Refusing to do the dangerous thing                                  */
/* ------------------------------------------------------------------ */

/**
 * A Safety is { scanComplete }.
 * Interpretation: what we know about the scan this plan was built from.
 * `scanComplete` is false when the shell could not read every file — a part
 * open in SolidWorks, a directory a backup tool made unreadable, a symbolic
 * link. Such a file is ABSENT from the local manifest, and an absent file is
 * indistinguishable from a deleted one, so the plan will offer to delete it
 * from Lattice for everybody. That offer must not be carried out.
 */
export interface Safety {
  readonly scanComplete: boolean
}

/** Where our own bookkeeping lives inside a linked folder; never synchronised. */
const LATTICE_DIR = '.lattice'

/**
 * Why we will not act on this path at all, or undefined when we will.
 *
 * WHY THE RENDERER CHECKS AS WELL AS THE SHELL
 *   These paths come from the server's replica, and two of the four actions —
 *   upload and delete-remote — never reach the shell's own `resolveWithin` at
 *   all. A path of "../../.ssh/authorized_keys" must be refused by whoever is
 *   about to act on it, and here that is sometimes us. Defence that exists in
 *   one place only is defence that moves when the code does.
 * pathRefusal : String -> String|undefined
 */
export function pathRefusal(path: string): string | undefined {
  if (typeof path !== 'string' || path.length === 0) return 'it has no name'
  if (path.includes('\0')) return 'the name contains a NUL byte'
  const posix = path.replace(/\\/g, '/')
  if (posix.startsWith('/') || /^[a-zA-Z]:/.test(posix)) return 'it is an absolute path, not one inside the folder'
  if (posix.startsWith('~')) return 'a name beginning with ~ is a home directory to some tools'
  const segments = posix.split('/')
  if (segments.some(segment => segment === '' || segment === '.' || segment === '..'))
    return 'it points outside the linked folder'
  if (segments[0] === LATTICE_DIR) return `${LATTICE_DIR}/ is Lattice's own bookkeeping`
  return undefined
}

/**
 * Split the planned work into what we are willing to do and what we are not.
 *
 * Interpretation: a withheld operation is NOT a failure of the person's
 * making, and it is not silently dropped either — it is reported by path and
 * reason, and because nothing is attempted for it, `settledResolutions` leaves
 * its base alone and it is offered again next time.
 * screenOperations : [FolderOperation] Safety -> {permitted, withheld}
 */
export function screenOperations(
  operations: readonly FolderOperation[], safety: Safety,
): { permitted: FolderOperation[]; withheld: Failure[] } {
  const permitted: FolderOperation[] = []
  const withheld: Failure[] = []
  for (const operation of operations) {
    const refusal = pathRefusal(operation.path)
    if (refusal) { withheld.push({ path: operation.path, error: `Lattice will not touch this path: ${refusal}` }); continue }
    // A deletion in Lattice is the one operation nobody else can undo, and an
    // incomplete scan is exactly the state that invents one.
    if (operation.action === 'delete-remote' && !safety.scanComplete) {
      withheld.push({
        path: operation.path,
        error: 'held back: some files could not be read in this scan, so a missing file may not really be gone. Close them and rescan.',
      })
      continue
    }
    permitted.push(operation)
  }
  return { permitted, withheld }
}

/** At most this many operations, or this many bytes, cross the bridge at once. */
export const CHUNK_OPERATIONS = 16
export const CHUNK_BYTES = 32_000_000

/**
 * Cut the work into pieces small enough to report progress between.
 *
 * The bridge answers once per call, so the chunk size IS the progress
 * granularity: one call for 900 files would be a spinner, and one call per
 * file would be 900 round trips. A chunk ends at whichever limit comes first,
 * so a batch of 3 MB drawings and a single 300 MB assembly both keep the bar
 * moving. A single operation larger than the byte limit still gets its own
 * chunk rather than being dropped.
 * chunkOperations : [FolderOperation] Number Number -> [[FolderOperation]]
 */
export function chunkOperations(
  operations: readonly FolderOperation[],
  maxOperations: number = CHUNK_OPERATIONS,
  maxBytes: number = CHUNK_BYTES,
): FolderOperation[][] {
  const chunks: FolderOperation[][] = []
  let current: FolderOperation[] = []
  let bytes = 0
  for (const operation of operations) {
    const size = bytesOfOperation(operation)
    if (current.length > 0 && (current.length >= maxOperations || bytes + size > maxBytes)) {
      chunks.push(current)
      current = []
      bytes = 0
    }
    current.push(operation)
    bytes += size
  }
  if (current.length > 0) chunks.push(current)
  return chunks
}

/**
 * The resolutions that ACTUALLY happened, given what the shell reported.
 *
 * A path whose operation failed, or was never attempted because the run was
 * cancelled, becomes "skip" — so the new pivot keeps its old value there and
 * the file is offered again next time. Recording work we did not do is the one
 * bug in a synchroniser that silently loses a file forever.
 * settledResolutions : SyncPlan Map<Path,Resolution> [OperationResult] -> Map<Path,Resolution>
 */
export function settledResolutions(
  plan: SyncPlan,
  chosen: ReadonlyMap<Path, Resolution>,
  results: readonly OperationResult[],
): Map<Path, Resolution> {
  const byPath = new Map(results.map(result => [result.path, result]))
  const settled = new Map<Path, Resolution>()
  for (const item of plan.items) {
    const resolution = chosen.get(item.path) ?? 'skip'
    const action = actionFor(item, resolution)
    // Nothing was asked of the shell, so there is nothing to have failed.
    if (action === 'nothing' || action === 'record-only') { settled.set(item.path, resolution); continue }
    settled.set(item.path, byPath.get(item.path)?.ok ? resolution : 'skip')
  }
  return settled
}

/**
 * What the buttons count.
 * Interpretation: `transfers` is how many files the shell would touch,
 * `bytes` how much it would move, `records` how many are already identical and
 * only need the manifest updating. Separating them is why "Sync 4" does not
 * become "Sync 137" because 133 files happen to already match.
 * countsFor : SyncPlan Map<Path,Resolution> -> {transfers, bytes, records}
 */
export function countsFor(plan: SyncPlan, resolutions: ReadonlyMap<Path, Resolution>): {
  transfers: number; bytes: number; records: number
} {
  const operations = operationsFor(plan, resolutions)
  const records = plan.items.filter(item => item.state === 'converged').length
  return { transfers: operations.length, bytes: bytesOf(operations), records }
}

/* ------------------------------------------------------------------ */
/* Running                                                             */
/* ------------------------------------------------------------------ */

/**
 * The run in flight, or undefined.
 *
 * Kept outside the store because a cancellation flag is not state the
 * interface renders — it is a token the loop checks — and putting a mutable
 * object in a zustand store makes every snapshot uncomparable.
 */
interface Token { cancelled: boolean; readonly projectId: string }

let current: Token | undefined

/**
 * Stop whatever is running. The work already done is kept and recorded.
 *
 * The slot is released IMMEDIATELY rather than when the abandoned call
 * returns. A promise cannot be un-awaited, but its answer is discarded, and
 * making the person wait for a result we have already decided to ignore before
 * they may press Rescan is a lie about what is happening.
 *
 * The SHELL is told as well, when it offers a way to be told. Without that,
 * an abandoned scan keeps reading 900 files off the disk for another minute
 * while the person is already asking for a new one — the answer is thrown
 * away, but the disk does not know that.
 */
export function cancelRun(): void {
  if (!current) return
  const { projectId } = current
  current.cancelled = true
  current = undefined
  useSyncFolder.getState().setProgress(IDLE)
  try {
    void folderBridge()?.cancel?.(projectId)?.catch(() => undefined)
  } catch {
    // An older shell has no cancel. Abandoning the answer is still correct.
  }
}

/** True when something is in flight. */
export const isRunning = (): boolean => current !== undefined

/** Take the token for a new piece of work, refusing to start a second one. */
function begin(phase: Phase, projectId: string, total = 0, bytesTotal = 0): Token | undefined {
  if (current) return undefined
  current = { cancelled: false, projectId }
  useSyncFolder.getState().setError(undefined)
  // Last time's summary belongs to last time; a new run starts with a clean slate.
  if (phase === 'applying') useSyncFolder.getState().setReport(undefined)
  useSyncFolder.getState().setProgress({ phase, done: 0, total, bytesDone: 0, bytesTotal })
  return current
}

/**
 * Give it back — but only if it is still ours. A cancelled run has already
 * handed the slot on, and must not reset the progress of whatever replaced it.
 */
function end(token: Token): void {
  if (current !== token) return
  current = undefined
  useSyncFolder.getState().setProgress(IDLE)
}

/** Whatever a thrown thing has to say for itself. messageOf : Any -> String */
const messageOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

/** Report a failure in words a person can act on. */
function fail(error: unknown): void {
  useSyncFolder.getState().setError(messageOf(error))
}

/**
 * Choose a folder and link this project to it, then look at what is in it.
 *
 * Linking does NOT move a single byte. It records where the folder is and
 * reads any manifest already sitting in it, so re-linking a folder you synced
 * last month remembers the pivot and does not offer to re-upload all of it.
 * linkProject : String -> Promise<void>
 */
export async function linkProject(projectId: string): Promise<void> {
  const bridge = folderBridge()
  if (!bridge) return
  const token = begin('choosing', projectId)
  if (!token) return
  try {
    const dir = await bridge.chooseFolder()
    if (!dir || token.cancelled) return
    const base = manifestFrom(await bridge.linkFolder(projectId, dir))
    useSyncFolder.getState().setLink({ projectId, dir, base, linkedAt: new Date().toISOString() })
  } catch (error) {
    fail(error)
    return
  } finally {
    end(token)
  }
  await rescan(projectId)
}

/**
 * Read the link the shell already holds for this project, if any.
 * Called when the Changes screen opens: the link lives on disk, not in this
 * window, so a reload must be able to find it again.
 * loadLink : String -> Promise<void>
 */
export async function loadLink(projectId: string): Promise<void> {
  const bridge = folderBridge()
  if (!bridge) return
  try {
    const record = await bridge.readLink(projectId)
    if (!record) { useSyncFolder.getState().dropLink(projectId); return }
    const existing = useSyncFolder.getState().links[projectId]
    useSyncFolder.getState().setLink({
      projectId,
      dir: record.dir,
      base: manifestFrom(record.manifest),
      linkedAt: existing?.linkedAt ?? new Date().toISOString(),
      ...(existing?.lastSyncedAt ? { lastSyncedAt: existing.lastSyncedAt } : {}),
    })
  } catch (error) {
    fail(error)
  }
}

/**
 * Hash everything in the linked folder.
 *
 * The slow one — this is where 900 files are read — so it reports progress and
 * can be abandoned. A cancelled scan is DISCARDED rather than half-applied: a
 * partial listing would look exactly like somebody having deleted the files it
 * never reached.
 * rescan : String -> Promise<void>
 */
export async function rescan(projectId: string): Promise<void> {
  const bridge = folderBridge()
  const link = useSyncFolder.getState().links[projectId]
  if (!bridge || !link) return
  const token = begin('scanning', projectId)
  if (!token) return

  const stop = bridge.onProgress?.(event => {
    if (token.cancelled || event.phase !== 'scanning') return
    useSyncFolder.getState().setProgress({
      phase: 'scanning',
      done: event.done,
      total: event.total,
      bytesDone: event.bytesDone ?? 0,
      bytesTotal: event.bytesTotal ?? 0,
      ...(event.path ? { path: event.path } : {}),
    })
  })

  try {
    const result = await bridge.scan(projectId, link.dir)
    if (token.cancelled) return
    useSyncFolder.getState().setScan({
      projectId,
      files: manifestFrom(result.files),
      at: new Date().toISOString(),
      skippedHashes: result.skippedHashes,
    })
  } catch (error) {
    fail(error)
  } finally {
    stop?.()
    end(token)
  }
}

/** Forget the folder. The files stay exactly where they are. */
export async function unlinkProject(projectId: string): Promise<void> {
  const bridge = folderBridge()
  useSyncFolder.getState().dropLink(projectId)
  try { await bridge?.unlink(projectId) } catch (error) { fail(error) }
}

/** Show the linked folder in Finder or Explorer. */
export async function revealFolder(projectId: string): Promise<void> {
  const link = useSyncFolder.getState().links[projectId]
  if (!link) return
  try { await folderBridge()?.revealFolder(link.dir) } catch (error) { fail(error) }
}

/**
 * Attach a download URL to every operation that needs one.
 *
 * Resolved one chunk at a time, immediately before that chunk runs, because a
 * signed URL expires: asking for 900 of them up front means the last ones have
 * gone stale by the time a slow link reaches them.
 * withUrls : String [FolderOperation] -> Promise<{operations, missing}>
 */
async function withUrls(projectId: string, chunk: readonly FolderOperation[]): Promise<{
  operations: FolderOperation[]; missing: Failure[]
}> {
  const wanted = chunk.filter(operation => operation.action === 'download')
  if (wanted.length === 0) return { operations: [...chunk], missing: [] }

  const answer = await api.downloadUrls(projectId, wanted.map(operation => operation.path))
  const byPath = new Map(answer.items.map(item => [item.path, item]))
  const operations: FolderOperation[] = []
  const missing: Failure[] = []
  for (const operation of chunk) {
    if (operation.action !== 'download') { operations.push(operation); continue }
    const item = byPath.get(operation.path)
    if (!item?.url) {
      missing.push({ path: operation.path, error: item?.reason ?? 'no download URL' })
      continue
    }
    // The URL is for whatever Lattice holds NOW, and the plan was built from
    // the replica as it was. If somebody committed a new revision in between,
    // the shell would fetch those bytes and reject them against the hash we
    // asked for — a correct refusal with an incomprehensible message. Say the
    // true thing instead, and leave the file for the next scan.
    if (operation.sha && item.sha256 && item.sha256 !== operation.sha) {
      missing.push({ path: operation.path, error: 'changed in Lattice since this scan — rescan and look again' })
      continue
    }
    operations.push({ ...operation, url: item.url })
  }
  return { operations, missing }
}

/**
 * The half of a chunk the shell performs: real files on this machine.
 * localOperations : [FolderOperation] -> [FolderOperation]
 */
const localOperations = (operations: readonly FolderOperation[]): FolderOperation[] =>
  operations.filter(o => o.action === 'download' || o.action === 'delete-local')

/**
 * Carry out the operations that belong to Lattice rather than to the disk.
 *
 * WHY THESE DO NOT CROSS THE BRIDGE
 *   An upload and a deletion in Lattice are HTTP requests against the API, and
 *   the renderer is what holds the session, the replica and the error
 *   handling. The shell answers "done" to both without contacting the server —
 *   deliberately, so that one list of operations can describe a whole sync —
 *   and taking that answer at face value would write "sent" into the pivot for
 *   a file that never left this machine. The next comparison would then read
 *   the unchanged copy in Lattice as somebody else's newer work and offer to
 *   pull it over the top. That is how a synchroniser eats a day's work.
 * applyRemote : String [FolderOperation] -> Promise<[OperationResult]>
 */
async function applyRemote(
  projectId: string, dir: string, operations: readonly FolderOperation[],
): Promise<OperationResult[]> {
  const results: OperationResult[] = []

  const removals = operations.filter(operation => operation.action === 'delete-remote')
  if (removals.length > 0) {
    const paths = removals.map(operation => operation.path)
    try {
      await api.deleteFiles({ projectId, paths, message: 'Deleted in a linked folder' })
      for (const path of paths) results.push({ path, ok: true })
    } catch (error) {
      for (const path of paths) results.push({ path, ok: false, error: messageOf(error) })
    }
  }

  for (const operation of operations) {
    if (operation.action === 'upload') results.push(await uploadOne(projectId, dir, operation))
  }
  return results
}

/**
 * Record one local file in Lattice, by hash.
 *
 * Three steps, and the order is the safety: ASK object storage whether it
 * already holds this content, SEND the bytes through the shell only if it does
 * not, and COMMIT the change only once the bytes are there. Content is
 * addressed by SHA-256, so a file Lattice already holds — one pulled and moved,
 * a part a colleague sent, a revision from this person's other machine — is
 * recorded with no transfer at all.
 *
 * Every way this can fall short returns ok:false with a reason. Nothing that
 * returns ok:false is written into the pivot, so an upload that did not happen
 * leaves the file outstanding rather than becoming, on the next comparison,
 * "somebody else's newer version" of the person's own work.
 * uploadOne : String String FolderOperation -> Promise<OperationResult>
 */
async function uploadOne(
  projectId: string, dir: string, operation: FolderOperation,
): Promise<OperationResult> {
  const { path, sha, size } = operation
  if (!sha || size === undefined) {
    return { path, ok: false, error: 'no hash for the local copy' }
  }

  try {
    // Ask first. Because storage is content-addressed, a file whose bytes
    // Lattice already holds — one pulled and moved, a part a colleague sent,
    // a revision from this person's other machine — needs no transfer at all.
    const ticket = await api.negotiateUpload(sha, size, contentTypeFor(path))

    if (ticket.kind === 'upload') {
      const bridge = folderBridge()
      if (!bridge?.sendFile) {
        return { path, ok: false, error: 'this build cannot send file contents from a folder' }
      }
      const sent = await bridge.sendFile({
        projectId, dir, path, url: ticket.url, method: ticket.method, headers: ticket.headers, sha,
      })
      if (!sent.ok) return { path, ok: false, error: sent.error ?? 'the upload did not finish' }
      await api.confirmUpload(sha)
    }

    await api.commitFile({
      projectId, path, sha256: sha, size, message: 'Sent from a linked folder',
    })
    return { path, ok: true }
  } catch (error) {
    return { path, ok: false, error: uploadProblem(error) }
  }
}

/**
 * What to tell object storage this file is.
 *
 * Only a hint: the content type decides how a preview opens it, never what is
 * stored. Anything unrecognised is an opaque stream, which is the honest
 * answer for a proprietary CAD format.
 * contentTypeFor : String -> String
 */
function contentTypeFor(path: string): string {
  const extension = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  const known: Record<string, string> = {
    pdf: 'application/pdf',
    step: 'model/step', stp: 'model/step',
    md: 'text/markdown', txt: 'text/plain', csv: 'text/csv', json: 'application/json',
    png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
    zip: 'application/zip', glb: 'model/gltf-binary',
  }
  return known[extension] ?? 'application/octet-stream'
}

/**
 * Why an upload did not happen, in the person's terms.
 *
 * `commitFile` answers 409 for exactly one situation: object storage does not
 * hold that hash. Reaching it here means the transfer above reported success
 * and the bytes still are not there, which is worth a sentence rather than a
 * status code. Everything else already carries the server's own words.
 * uploadProblem : Any -> String
 */
function uploadProblem(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'the bytes did not reach storage, so nothing was recorded'
  }
  return messageOf(error)
}

/**
 * Carry out one set of resolutions.
 *
 * The three buttons are three calls to this: Pull passes core's `pullOnly`,
 * Push passes `pushOnly`, and Sync passes what the person actually chose. None
 * of them can settle a conflict, because none of those maps ever contains
 * anything but "skip" for one.
 *
 * WHO DOES WHAT
 *   Downloads and local deletions go to the shell, which owns the disk.
 *   Uploads and deletions in Lattice are performed HERE against the API — see
 *   `applyRemote` for why taking the shell's word for those loses files.
 *
 * Afterwards the new pivot is written into the folder and the folder is
 * rescanned — the manifest must describe what is REALLY on disk, not what we
 * asked for, and only a rescan can tell us that.
 * runSync : String SyncPlan Map<Path,Resolution> Kind -> Promise<RunReport|undefined>
 */
export async function runSync(
  projectId: string,
  plan: SyncPlan,
  resolutions: ReadonlyMap<Path, Resolution>,
  kind: RunReport['kind'],
): Promise<RunReport | undefined> {
  const bridge = folderBridge()
  const link = useSyncFolder.getState().links[projectId]
  if (!bridge || !link) return undefined

  // What the plan ASKS for, minus what we refuse to do with the scan we have.
  const scan = useSyncFolder.getState().scan
  const { permitted, withheld } = screenOperations(operationsFor(plan, resolutions), {
    scanComplete: scan?.projectId === projectId && scan.skippedHashes === 0,
  })
  const chunks = chunkOperations(permitted)
  const token = begin('applying', projectId, permitted.length, bytesOf(permitted))
  if (!token) return undefined

  const bytesTotal = bytesOf(permitted)
  const byPath = new Map(permitted.map(operation => [operation.path, operation]))
  const results: OperationResult[] = []
  const failures: Failure[] = [...withheld]
  let done = 0
  let bytesDone = 0

  // A failure here STOPS the run but does not discard it: whatever the shell
  // has already done is real, and the pivot below must record it. Losing that
  // record would make the next scan report finished work as outstanding.
  try {
    for (const chunk of chunks) {
      if (token.cancelled) break
      const { operations, missing } = await withUrls(projectId, chunk)
      failures.push(...missing)
      for (const gone of missing) results.push({ path: gone.path, ok: false, error: gone.error })

      // Each answer is recorded the moment it arrives, never held in a
      // variable that a later `break` could throw away: a result we drop is a
      // completed operation the pivot will not know about.
      const record = (answers: readonly OperationResult[]): void => {
        results.push(...answers)
        for (const result of answers) {
          if (!result.ok) failures.push({ path: result.path, error: result.error ?? 'failed' })
        }
      }

      const forShell = localOperations(operations)
      if (forShell.length > 0) record(await bridge.apply(projectId, link.dir, forShell))
      record(await applyRemote(projectId, link.dir, operations))

      done += chunk.length
      bytesDone += bytesOf(chunk)
      useSyncFolder.getState().setProgress({
        phase: 'applying',
        done,
        total: permitted.length,
        bytesDone,
        bytesTotal,
        ...(chunk[chunk.length - 1] ? { path: chunk[chunk.length - 1].path } : {}),
      })
    }
  } catch (error) {
    fail(error)
  }

  try {
    const settled = settledResolutions(plan, resolutions, results)
    const base = nextManifest(link.base, plan, settled)
    await bridge.writeManifest(projectId, link.dir, manifestPairs(base))
    useSyncFolder.getState().setLink({ ...link, base, lastSyncedAt: new Date().toISOString() })
  } catch (error) {
    fail(error)
  }

  // Bytes MOVED, not bytes attempted: a chunk whose transfer failed is not
  // progress, and a summary that counts it teaches people to distrust the
  // summary.
  const moved = results
    .filter(result => result.ok)
    .reduce((sum, result) => sum + bytesOfOperation(byPath.get(result.path)), 0)

  const report: RunReport = {
    at: new Date().toISOString(),
    kind,
    applied: results.filter(result => result.ok).length,
    bytes: moved,
    failures,
    cancelled: token.cancelled || done < permitted.length,
  }
  useSyncFolder.getState().setReport(report)

  end(token)
  // What is on disk NOW is a question only a scan can answer: the shell may
  // have written something other than what we asked for, and pretending
  // otherwise is how a manifest starts describing a folder that moved on.
  await rescan(projectId)
  return report
}

/* ------------------------------------------------------------------ */
/* Small shared helpers                                                */
/* ------------------------------------------------------------------ */

/**
 * A one-slot memo keyed on object identity, as used throughout the stores.
 * The replica replaces its maps rather than mutating them, so identity is a
 * correct and extremely cheap cache key.
 * memoOnce : (A -> B) -> (A -> B)
 */
function memoOnce<A extends object, B>(f: (a: A) => B): (a: A) => B {
  let lastInput: A | undefined
  let lastOutput: B
  return (a: A): B => {
    if (a !== lastInput) { lastInput = a; lastOutput = f(a) }
    return lastOutput
  }
}
